import React, { useContext, useEffect, useState } from "react";
import Moment from "moment";
import { IResourceComponentsProps } from "@refinedev/core";
import { List } from "@refinedev/mui";
import {
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  getGridStringOperators,
} from "@mui/x-data-grid";

import { database } from "utility/appwriteClient";
import { TentantContext } from "contexts/tentant";
import { PluginList, Stop, Route, FormattedRoute } from "interfaces";

import config from "../../appconfig.json";
import * as XLSX from "xlsx";

export const Deliveries: React.FC<IResourceComponentsProps> = () => {
  const [deliveries, setDeliveries] = useState<FormattedRoute[]>([]);
  const [plugins, setPlugins] = useState<PluginList[]>([]);
  const [tentant] = useContext(TentantContext);
  const [pageCount, setPageCount] = useState<number>(0);
  const [driver, setDriver] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const pageChangeHandler = (e: any) => {
    setPaginationModel(e);
    setPageCount(e.page * 10);
  };

  useEffect(() => {
    database
      .listDocuments(tentant.toLowerCase(), tentant.toLowerCase() + "_plugin")
      .then((response) => {
        setPlugins(response.documents as unknown as PluginList[]);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    fetchDeliveries().then((f) => {
      setDeliveries(formatDeliveries(f.routes));
    });
  }, []);

  function formatDeliveries(routes: Route[]): FormattedRoute[] {
    return routes.flatMap((route: Route) => {
      const totalBoxes = route.load1 + route.load2 + route.load3 + route.load4;
      return route.stops.map((stop: Stop) => ({
        orderNo: stop.orderNo,
        driverName: route.driverName,
        stopNumber: stop.stopNumber,
        totalBoxes,
        location: stop.locationName,
        address: stop.address,
        schedule: stop.scheduledAtDt,
      }));
    });
  }

  const fetchDeliveries = async () => {
    try {
      const response = await fetch(
        config.optimoRoute
          .replace("{KEY}", config.optimoRouteKey)
          .replace("{DATE}", Moment().format("YYYY-MM-DD")),
        {
          method: "GET",
        }
      );

      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const rows = deliveries.flatMap((document) => ({
    id: document.orderNo,
    orderNo: document.orderNo,
    driverName: document.driverName,
    stopNumber: document.stopNumber,
    totalBoxes: document.totalBoxes,
    location: document.location,
    address: document.address,
    schedule: document.schedule,
  }));

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ["contains"].includes(value)
  );

  const columns: GridColDef[] = [
    { field: "orderNo", headerName: "Order ID", flex: 1, filterOperators },
    { field: "driverName", headerName: "Driver", flex: 1, filterOperators },
    {
      field: "stopNumber",
      headerName: "Stop Number",
      flex: 1,
      filterOperators,
    },
    {
      field: "totalBoxes",
      headerName: "Total Boxes",
      flex: 1,
      filterOperators,
    },
    { field: "location", headerName: "Location", flex: 1, filterOperators },
    { field: "address", headerName: "Address", flex: 1, filterOperators },
    { field: "schedule", headerName: "Schedule", flex: 1, filterOperators },
  ];

  const handleChange = (event: SelectChangeEvent) => {
    const selectedDriver = event.target.value;
    setDriver(selectedDriver);

    fetchDeliveries().then((f) => {
      const filteredDeliveries = formatDeliveries(f.routes).filter(
        (m) => m.driverName == selectedDriver
      );
      setDeliveries(filteredDeliveries);
    });
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(deliveries);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const today = new Date().toISOString().slice(0, 10).replace(/-/g, "_");
    const driverFilter = driver.replace(/[-\s]+/g, "_");;
    const filename = `${today}_${driverFilter}_Deliveries.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  return (
    <Box
      sx={{
        p: 4,
        borderRadius: 4,
        backgroundColor: "background.paper",
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} lg={12}>
          <Grid item xs={12} lg={12}>
            <List
              wrapperProps={{
                sx: {
                  paddingX: { xs: 2, md: 0 },
                  boxShadow: "0",
                  backgroundColor: "background.paper",
                  backgroundImage:
                    "linear-gradient(rgba(255, 255, 255, 0.00), rgba(255, 255, 255, 0.00))",
                },
              }}
              headerProps={{
                action: (
                  <div>
                    <FormControl sx={{ m: 1, minWidth: 200 }}>
                      <InputLabel>Driver</InputLabel>
                      <Select
                        label="Driver"
                        autoWidth
                        value={driver}
                        onChange={handleChange}
                      >
                        <MenuItem value="Andrew - East">Andrew - East</MenuItem>
                        <MenuItem value="Nish - Central">
                          Nish - Central
                        </MenuItem>
                        <MenuItem value="George - South">
                          George - South
                        </MenuItem>
                        <MenuItem value="Keshara - West">
                          Keshara - West
                        </MenuItem>
                        <MenuItem value="Omar - Central">
                          Omar - Central
                        </MenuItem>
                        <MenuItem value="Heshan - Inner East">
                          Heshan - Inner East
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                ),
              }}
            >
              <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={(e) => pageChangeHandler(e)}
              />
              <Button onClick={downloadExcel}>Download Excel</Button>
            </List>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
